import { PublishedCard } from "../types";
import { CreateCardTranslate } from "./createCardTransformShared";
import { PageData } from "./useTemplatePageHelpers";

const normalizeTypeKey = (type?: string) =>
  (type || "").replace(/_/g, "").replace(/\s+/g, "").toLowerCase().trim();

const getEndingImageUrlFromContent = (content: any) => {
  if (!content) return "";
  if (typeof content.imageUrl === "string" && content.imageUrl.trim()) {
    return content.imageUrl;
  }
  if (typeof content.url === "string" && content.url.trim()) {
    return content.url;
  }
  if (Array.isArray(content.url) && typeof content.url[0] === "string") {
    return content.url[0] || "";
  }
  return "";
};

const inferMediaTypeFromUrl = (url: string): "video" | "audio" | "" => {
  if (!url) return "";
  const lower = url.toLowerCase();
  if (/\.(mp4|mov|avi|webm|mkv)(\?|$)/.test(lower)) return "video";
  if (/\.(mp3|wav|ogg|aac|flac)(\?|$)/.test(lower)) return "audio";
  return "";
};

export function getCreateTaasCardTemplateFlags(card: PublishedCard | null, t: CreateCardTranslate) {
  const isSingleCardTemplate =
    card?.template === t("single_card_template", { ns: "taascard" }) ||
    card?.template === "Single Card Template";
  const isKIndieCardTemplate =
    card?.template === t("k_indie_card_template", { ns: "taascard" }) ||
    card?.template === "K-Indie Card Template";
  const isDemoCardTemplate = card?.template === "Demo Card Template";
  const isListeningTogetherCardTemplate = card?.template === "Listening Together Card Template";
  const usesEndingImage =
    isSingleCardTemplate ||
    isKIndieCardTemplate ||
    isDemoCardTemplate ||
    isListeningTogetherCardTemplate;
  const isGiftCardTemplate = card?.template === t("gift_card_template", { ns: "taascard" });

  return { usesEndingImage, isGiftCardTemplate };
}

export interface BuildCreateTaasCardPagesOptions {
  preservePageIds?: boolean;
}

export function buildCreateTaasCardPages(
  card: PublishedCard,
  t: CreateCardTranslate,
  options: BuildCreateTaasCardPagesOptions = {}
): PageData[] {
  const { preservePageIds = false } = options;
  const { usesEndingImage } = getCreateTaasCardTemplateFlags(card, t);
  const allPages: PageData[] = [];

  const isFanClubCardTemplateCheck =
    card.template === t("fan_club_card_template", { ns: "taascard" }) ||
    card.template === "Fan Club Card Template";
  const isBMCardTemplateCheck =
    card.template === t("bm_card_template", { ns: "taascard" }) ||
    card.template === "BM Card Template";

  allPages.push({
    order: 1,
    type: "CoverImage",
    content: { imageUrl: card.coverImageUrl || "" },
  });

  if (card.pages?.length) {
    let orderIndex = 2;
    card.pages.forEach((page, pageIdx) => {
      let pageType = page.type || "";
      let pageContent = page.content || {};
      const isLastTemplatePage = pageIdx === card.pages.length - 1;

      if ((isFanClubCardTemplateCheck || isBMCardTemplateCheck) && pageIdx === 1) {
        pageType = t("welcome", { ns: "taascard" });
        const raw = page.content || {};
        const mediaUrl = raw.url || raw.media_url || raw.video_url || raw.audio_url || "";
        pageContent = {
          title: raw.title || "",
          content: raw.description || raw.content || "",
          media_type: inferMediaTypeFromUrl(mediaUrl),
          media_url: mediaUrl,
        };
      }

      if (usesEndingImage && isLastTemplatePage && normalizeTypeKey(pageType) === "coverimage") {
        pageType = "EndingImage";
        pageContent = {
          imageUrl: getEndingImageUrlFromContent(pageContent) || card.coverImageUrl || "",
        };
      }

      const pageKey = normalizeTypeKey(pageType);
      if (pageKey === "singlecard" || pageKey === "kindiecard") {
        const sealFromPage = typeof pageContent.seal_time === "string" ? pageContent.seal_time : "";
        const sealFromCard = typeof card.seal_time === "string" ? card.seal_time : "";
        pageContent = {
          ...pageContent,
          seal_time: sealFromPage.trim() || sealFromCard.trim() || "",
        };
      }

      const sourcePageId = (page as { id?: number | string })?.id;
      const numericId =
        sourcePageId !== undefined && sourcePageId !== null ? Number(sourcePageId) : undefined;

      allPages.push({
        order: orderIndex,
        type: pageType,
        content: pageContent,
        ...(preservePageIds && Number.isFinite(numericId) ? { id: numericId } : {}),
      });
      orderIndex += 1;

      if (
        (page.type === "Instant_Card" ||
          page.type === "InstantCard" ||
          page.type === "instant_card") &&
        Array.isArray(page.content?.songs) &&
        page.content.songs.length > 0
      ) {
        allPages.push({
          order: orderIndex,
          type: "Songs",
          content: { songs: page.content.songs },
        });
        orderIndex += 1;
      }
    });
  }

  return allPages;
}

// ── Backend type normalization ────────────────────────────────────────────────
// The FE uses `Listening_Together_Card` everywhere (filters, deck routes,
// renderers), but the create-card endpoint's `type` enum on the backend
// accepts only `Listen_Together_Card` (no "ing"). Without this mapping the
// backend rejects the submit with:
//   "type must be one of the following values: ..., Listen_Together_Card"
// Map FE-side page type values to the values the backend `type` enum accepts.
// Keep the FE state untouched everywhere else; only rewrite at submit time.
const FE_TO_BACKEND_PAGE_TYPE: Readonly<Record<string, string>> = {
  Listening_Together_Card: "Listen_Together_Card",
};

export function normalizePageTypeForBackend(type: string | undefined): string {
  if (!type) return "";
  return FE_TO_BACKEND_PAGE_TYPE[type] ?? type;
}

/** Returns a new array where every page's `type` has been mapped to its
 *  backend-accepted value. Pages whose type isn't in the map are returned
 *  by reference, so the array allocates only when something actually
 *  changes. */
export function normalizePagesForBackend(pages: PageData[]): PageData[] {
  let mutated = false;
  const next = pages.map((page) => {
    const mapped = normalizePageTypeForBackend(page.type);
    if (mapped === page.type) return page;
    mutated = true;
    return { ...page, type: mapped };
  });
  return mutated ? next : pages;
}

export function formatCreateTaasCardPageType(type: string | undefined): string {
  if (!type) return "Unknown";
  if (type === "EndingImage") return "Ending Image";
  const normalized = type
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim();
  return normalized || type;
}

// ── User-addable page types (Single / Demo / Listening Together) ──────────────

export type AddablePageType =
  | "Youtube"
  | "Image"
  | "Video"
  | "Text"
  | "Audio"
  | "Post_Card"
  | "News"
  | "About"
  | "Bonus"
  | "Instrument";

export const SINGLE_CARD_ADDABLE_TYPES: AddablePageType[] = [
  "Youtube",
  "Image",
  "Video",
  "Text",
  "Audio",
  "Post_Card",
  "News",
  "About",
  "Bonus",
  "Instrument",
];

export const DEMO_ADDABLE_TYPES: AddablePageType[] = ["Youtube", "Audio", "Video", "Image", "Text"];

export const SEASON_ADDABLE_TYPES: AddablePageType[] = [
  "Youtube",
  "Audio",
  "Video",
  "Image",
  "Text",
];

export function getAddablePageTypesForTemplate(
  card: PublishedCard | null,
  t: CreateCardTranslate
): AddablePageType[] {
  if (!card) return [];
  const isSingleCardTemplate =
    card.template === t("single_card_template", { ns: "taascard" }) ||
    card.template === "Single Card Template";
  const isDemoCardTemplate = card.template === "Demo Card Template";

  if (isSingleCardTemplate) return SINGLE_CARD_ADDABLE_TYPES;
  if (isDemoCardTemplate) return DEMO_ADDABLE_TYPES;

  return [];
}

/** Empty page content shape per type. Matches keys consumed by
 *  `UseTemplatePageRenderer`, `getTemplatePageValidationMessages`, and
 *  `submitTemplateCard` so the new page integrates with edit, validation,
 *  and submit flows without further wiring. */
export function buildNewPageContent(type: AddablePageType): any {
  switch (type) {
    case "Youtube":
      return { title: "", description: "", url: "" };
    case "Image":
      return { images: [], description: "", url: [] };
    case "Video":
      return { videoUrl: "", description: "", url: "" };
    case "Text":
      return { title: "", description: "" };
    case "Audio":
      return { audioUrl: "", url: "", description: "" };
    case "Post_Card":
      return { title: "", description: "", url: "", signature: "" };
    case "News":
      return [{ title: "", description: "", url: "" }];
    case "About":
      return {
        title: "",
        description: "",
        descriptionImage: "",
        extraDescription: "",
        extraDescriptionImage: "",
        moodNote: "",
        momentImage: "",
        moments: [""],
        credits: [""],
      };
    case "Bonus":
      return {
        title: "",
        description: "",
        voiceNote: "",
        image: "",
        demoSnippet: "",
        cardLinks: [],
      };
    case "Instrument":
      return {
        title: "",
        url: "",
        artistName: "",
        description: "",
      };
    default:
      return {};
  }
}

/** Insert position for a user-added page: just before the EndingImage page
 *  (when present), otherwise at the end. */
export function getAddablePageInsertionIndex(pages: PageData[]): number {
  if (pages.length === 0) return 0;
  const lastIdx = pages.length - 1;
  if (normalizeTypeKey(pages[lastIdx]?.type) === "endingimage") {
    return lastIdx;
  }
  return pages.length;
}

/** Returns a new pages array with the new page inserted and `order`
 *  re-sequenced from 1. */
export function insertNewTemplatePage(pages: PageData[], type: AddablePageType): PageData[] {
  const insertionIndex = getAddablePageInsertionIndex(pages);
  const newPage: PageData = {
    order: insertionIndex + 1,
    type,
    content: buildNewPageContent(type),
  };
  const next: PageData[] = [
    ...pages.slice(0, insertionIndex),
    newPage,
    ...pages.slice(insertionIndex),
  ];
  return next.map((page, idx) => ({ ...page, order: idx + 1 }));
}

/** Remove the page at `index` and re-sequence `order` values. Returns the
 *  same array reference (unchanged) when `index` is out of range. */
export function removeTemplatePage(pages: PageData[], index: number): PageData[] {
  if (index < 0 || index >= pages.length) return pages;
  const next = [...pages.slice(0, index), ...pages.slice(index + 1)];
  return next.map((page, idx) => ({ ...page, order: idx + 1 }));
}

export const isEndingImagePage = (page: PageData | undefined): boolean =>
  normalizeTypeKey(page?.type) === "endingimage";

// ── Page lock configuration per template ──────────────────────────────────────
// `compulsoryCount`     — number of leading pages (by current index) that can
//                          never be removed. Stays put as users add pages.
// `lockEndingImage`     — when true, an EndingImage page is also non-removable
//                          regardless of its position.
// Templates not listed here return `null` → no cross is shown anywhere.

export interface PageLockConfig {
  compulsoryCount: number;
  lockEndingImage: boolean;
}

export function getPageLockConfigForTemplate(
  card: PublishedCard | null,
  t: CreateCardTranslate
): PageLockConfig | null {
  if (!card) return null;
  const isSingleCardTemplate =
    card.template === t("single_card_template", { ns: "taascard" }) ||
    card.template === "Single Card Template";
  const isDemoCardTemplate = card.template === "Demo Card Template";
  const isListeningTogetherCardTemplate = card.template === "Listening Together Card Template";

  if (isSingleCardTemplate) {
    return { compulsoryCount: 3, lockEndingImage: true };
  }
  if (isDemoCardTemplate || isListeningTogetherCardTemplate) {
    return { compulsoryCount: 2, lockEndingImage: false };
  }
  return null;
}

/** A page can be removed when:
 *  - the template has a lock config (templates without one show no cross), AND
 *  - the page's index is at or beyond `compulsoryCount`, AND
 *  - the page is not an EndingImage when `lockEndingImage` is true. */
export function isRemovablePage(
  page: PageData | undefined,
  index: number,
  lockConfig: PageLockConfig | null
): boolean {
  if (!page || !lockConfig) return false;
  if (index < lockConfig.compulsoryCount) return false;
  if (lockConfig.lockEndingImage && isEndingImagePage(page)) return false;
  return true;
}
